import React, { useMemo } from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Grid,
  MenuItem,
  TextField,
  Typography,
  createFilterOptions,
} from '@mui/material';
import type { AIEventType, AlertSummary } from '../../types';

interface EventTypeAutocompleteProps {
  eventTypes: AIEventType[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  loading?: boolean;
  accessError?: string | null;
  required?: boolean;
  isAdmin?: boolean;
  label?: string;
}

const categoryLabels: Record<string, string> = {
  alerts: 'Alerts',
  fundamental: 'Economic Calendar',
  fundamentals: 'Fundamentals',
  knowledge_graph: 'Knowledge Graph',
  screener: 'Screener',
  trading: 'Trading',
};

interface EventFilterFieldsProps {
  eventType?: AIEventType | null;
  value: EventFilterFormValue;
  onChange: (value: EventFilterFormValue) => void;
  disabled?: boolean;
  alerts?: AlertSummary[];
  alertsLoading?: boolean;
}

export interface EventFilterFormValue {
  symbols: string;
  currencies: string;
  severity: string;
  payloadFields: Record<string, string>;
}

export const createEmptyEventFilterValue = (): EventFilterFormValue => ({
  symbols: '',
  currencies: '',
  severity: '',
  payloadFields: {},
});

const formatCategory = (category: string) =>
  categoryLabels[category] ||
  category
    .split(/[_-]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const filterEventTypes = createFilterOptions<AIEventType>({
  stringify: (option) =>
    [
      option.label,
      option.value,
      option.description,
      option.category,
      formatCategory(option.category),
    ].join(' '),
});

const splitList = (value: string): string[] =>
  value
    .split(',')
    .map((item) => item.trim().toUpperCase())
    .filter(Boolean);

const joinList = (value: unknown): string => (Array.isArray(value) ? value.join(', ') : '');

const toFieldLabel = (field: string): string => {
  const labels: Record<string, string> = {
    alert_id: 'Alert ID',
    close_price: 'Close Price',
    edge_type: 'Edge Type',
    event_type: 'Market Event Type',
    fiscal_period: 'Fiscal Period',
    fiscal_year: 'Fiscal Year',
    pending_id: 'Approval ID',
    source_display: 'Source',
    statement_type: 'Statement Type',
  };
  return (
    labels[field] ||
    field
      .split(/[_-]/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ')
  );
};

const alertOptionLabel = (alert: AlertSummary) =>
  `${alert.name} - ${alert.symbol} ${alert.timeframe}`;

const getPayloadFilterFields = (eventType?: AIEventType | null) =>
  (eventType?.filter_fields || [])
    .filter((field) => field.startsWith('payload.') && field !== 'payload.*')
    .map((field) => field.replace(/^payload\./, ''));

export const buildEventFilters = (
  eventType: AIEventType | null | undefined,
  value: EventFilterFormValue
): Record<string, any> => {
  const filterFields = new Set(eventType?.filter_fields || []);
  const filters: Record<string, any> = {};

  if (filterFields.has('symbols')) {
    const symbols = splitList(value.symbols);
    if (symbols.length) filters.symbols = symbols;
  }

  if (filterFields.has('currencies')) {
    const currencies = splitList(value.currencies);
    if (currencies.length) filters.currencies = currencies;
  }

  if (filterFields.has('severity') && value.severity) {
    filters.severity = value.severity;
  }

  getPayloadFilterFields(eventType).forEach((field) => {
    const rawValue = value.payloadFields[field]?.trim();
    if (rawValue) {
      filters[`payload.${field}`] = rawValue;
    }
  });

  return filters;
};

export const eventFilterValueFromFilters = (
  filters?: Record<string, any> | null
): EventFilterFormValue => {
  const payloadFields: Record<string, string> = {};
  Object.entries(filters || {}).forEach(([field, value]) => {
    if (field.startsWith('payload.') && value != null) {
      payloadFields[field.replace(/^payload\./, '')] = String(value);
    }
  });

  return {
    symbols: joinList(filters?.symbols),
    currencies: joinList(filters?.currencies),
    severity: typeof filters?.severity === 'string' ? filters.severity : '',
    payloadFields,
  };
};

export const getAccessibleEventTypes = (
  eventTypes: AIEventType[],
  _isAdmin = false
): AIEventType[] => {
  // The backend event-types endpoint is the source of truth for subscription
  // and admin access. The frontend only sorts the already-authorized list.
  return [...eventTypes].sort((a, b) => {
    const categoryCompare = formatCategory(a.category).localeCompare(formatCategory(b.category));
    if (categoryCompare !== 0) return categoryCompare;
    return a.label.localeCompare(b.label);
  });
};

export const eventTypeAccessErrorMessage = (error: unknown): string | null => {
  if (!error) return null;
  const response = (error as any)?.response;
  const detail = response?.data?.detail;
  if (response?.status === 401) {
    return detail || 'Sign in to view event trigger types.';
  }
  if (response?.status === 402 || response?.status === 403) {
    return detail || 'Your current plan does not include event trigger types.';
  }
  return detail || 'Unable to load event trigger types.';
};

export const EventFilterFields: React.FC<EventFilterFieldsProps> = ({
  eventType,
  value,
  onChange,
  disabled = false,
  alerts = [],
  alertsLoading = false,
}) => {
  const filterFields = new Set(eventType?.filter_fields || []);
  const payloadFields = getPayloadFilterFields(eventType);
  const isAlertTrigger = eventType?.value === 'alerts.triggered';

  if (!eventType || (filterFields.size === 0 && payloadFields.length === 0)) {
    return null;
  }

  const update = (patch: Partial<EventFilterFormValue>) => {
    onChange({
      ...value,
      ...patch,
      payloadFields: patch.payloadFields || value.payloadFields,
    });
  };

  const updatePayloadField = (field: string, fieldValue: string) => {
    update({
      payloadFields: {
        ...value.payloadFields,
        [field]: fieldValue,
      },
    });
  };

  if (isAlertTrigger) {
    const selectedAlert =
      alerts.find((alert) => alert.id === value.payloadFields.alert_id) || null;

    return (
      <Grid size={{ xs: 12 }}>
        <Autocomplete
          fullWidth
          options={alerts}
          value={selectedAlert}
          onChange={(_, alert) =>
            update({
              payloadFields: {
                ...value.payloadFields,
                alert_id: alert?.id || '',
              },
            })
          }
          getOptionLabel={alertOptionLabel}
          isOptionEqualToValue={(option, selected) => option.id === selected.id}
          loading={alertsLoading}
          disabled={disabled}
          noOptionsText={alertsLoading ? 'Loading alerts...' : 'No alerts available'}
          renderOption={(props, alert) => (
            <Box component="li" {...props} key={alert.id}>
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {alert.name}
                  </Typography>
                  <Chip
                    size="small"
                    label={alert.is_active ? 'Active' : 'Inactive'}
                    color={alert.is_active ? 'success' : 'default'}
                    variant="outlined"
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {alert.symbol} / {alert.timeframe}
                </Typography>
              </Box>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Alert"
              required
              helperText="Run this AI system when the selected alert triggers."
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {alertsLoading && <CircularProgress color="inherit" size={18} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </Grid>
    );
  }

  return (
    <>
      {filterFields.has('symbols') && (
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            fullWidth
            label="Symbols"
            value={value.symbols}
            onChange={(event) => update({ symbols: event.target.value })}
            placeholder="AAPL, EURUSD"
            disabled={disabled}
          />
        </Grid>
      )}
      {filterFields.has('currencies') && (
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            fullWidth
            label="Currencies"
            value={value.currencies}
            onChange={(event) => update({ currencies: event.target.value })}
            placeholder="USD, EUR"
            disabled={disabled}
          />
        </Grid>
      )}
      {filterFields.has('severity') && (
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            select
            fullWidth
            label="Severity"
            value={value.severity}
            onChange={(event) => update({ severity: event.target.value })}
            disabled={disabled}
          >
            <MenuItem value="">Any</MenuItem>
            <MenuItem value="INFO">Info</MenuItem>
            <MenuItem value="NOTICE">Notice</MenuItem>
            <MenuItem value="WARNING">Warning</MenuItem>
            <MenuItem value="ERROR">Error</MenuItem>
            <MenuItem value="CRITICAL">Critical</MenuItem>
          </TextField>
        </Grid>
      )}
      {payloadFields.map((field) => (
        <Grid key={field} size={{ xs: 12, md: 4 }}>
          <TextField
            fullWidth
            label={toFieldLabel(field)}
            value={value.payloadFields[field] || ''}
            onChange={(event) => updatePayloadField(field, event.target.value)}
            disabled={disabled}
          />
        </Grid>
      ))}
    </>
  );
};

export const EventTypeAutocomplete: React.FC<EventTypeAutocompleteProps> = ({
  eventTypes,
  value,
  onChange,
  disabled = false,
  loading = false,
  accessError = null,
  required = false,
  isAdmin = false,
  label = 'Event Type',
}) => {
  const options = useMemo(
    () => getAccessibleEventTypes(eventTypes, isAdmin),
    [eventTypes, isAdmin]
  );
  const selectedOption = options.find((option) => option.value === value) || null;

  return (
    <Autocomplete
      fullWidth
      options={options}
      value={selectedOption}
      onChange={(_, option) => onChange(option?.value || '')}
      groupBy={(option) => formatCategory(option.category)}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, selected) => option.value === selected.value}
      filterOptions={filterEventTypes}
      loading={loading}
      disabled={disabled}
      noOptionsText={accessError || (loading ? 'Loading event types...' : 'No accessible event types')}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.value} sx={{ alignItems: 'flex-start' }}>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {option.label}
              </Typography>
              {option.admin_only && (
                <Chip size="small" label="Admin" color="warning" variant="outlined" />
              )}
              {!option.processed && (
                <Chip size="small" label="Raw" color="default" variant="outlined" />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {option.value}
            </Typography>
            {option.description && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {option.description}
              </Typography>
            )}
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={Boolean(accessError)}
          helperText={
            accessError ||
            'Search by name, category, or event id. Only event types available to your account are listed.'
          }
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress color="inherit" size={18} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};
